import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { LogOut, Shield, Mail, Trash2, Bell, KeyRound, Copy, Ban } from "lucide-react";
import { toast } from "sonner";

const PrefRow = ({ label, hint, checked, onChange, testid }) => (
    <label className="flex items-start justify-between gap-3 py-3 border-b border-border-line last:border-0">
        <div className="flex-1">
            <div className="text-sm font-medium text-ink">{label}</div>
            {hint && <div className="text-xs text-muted mt-0.5">{hint}</div>}
        </div>
        <input data-testid={testid} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-5 h-5 accent-bordeaux" />
    </label>
);

const InviteCodes = () => {
    const [items, setItems] = useState(null);
    const [note, setNote] = useState("");
    const [singleUse, setSingleUse] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/admin/invites"); setItems(data); } catch { setItems([]); }
    };
    useEffect(() => { load(); }, []);

    const copy = async (code) => {
        try { await navigator.clipboard.writeText(code); toast.success(`Codice ${code} copiato`); }
        catch { toast.message(`Codice: ${code}`); }
    };

    const create = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/invites", { note, single_use: singleUse });
            setNote("");
            await load();
            copy(data.code);
        } catch { toast.error("Errore nella creazione del codice"); }
        finally { setBusy(false); }
    };

    const disable = async (code) => {
        try { await api.delete(`/admin/invites/${code}`); toast.success("Codice disattivato"); load(); }
        catch { toast.error("Errore"); }
    };

    return (
        <section className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-6" data-testid="invite-codes">
            <div className="flex items-center gap-2 mb-1">
                <KeyRound size={20} className="text-bordeaux" />
                <h2 className="font-heading text-2xl text-ink">Codici d'invito</h2>
            </div>
            <p className="text-sm text-muted mb-4">
                Solo chi ha un codice può iscriversi. Generane uno per ogni ospite dopo l'intervista e mandaglielo:
                lo inserisce una sola volta, al momento dell'iscrizione.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Per chi è? (es. Mario Rossi)" className="input-clean flex-1" data-testid="invite-note" />
                <button onClick={create} disabled={busy} className="btn-primary text-sm justify-center" data-testid="invite-create">
                    {busy ? "Attendi…" : "Genera codice"}
                </button>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-muted cursor-pointer">
                <input type="checkbox" checked={!singleUse} onChange={(e) => setSingleUse(!e.target.checked)} className="w-4 h-4 accent-bordeaux" />
                Codice generico (valido per più persone finché non lo disattivi)
            </label>
            <div className="mt-5 divide-y divide-border-line">
                {items === null ? <div className="text-sm text-muted">Caricamento…</div> :
                 items.length === 0 ? <div className="text-sm text-muted">Nessun codice creato.</div> :
                 items.map((i) => {
                    const used = i.single_use && i.used_by_email;
                    const status = !i.active ? "Disattivato" : used ? `Usato da ${i.used_by_email}` : i.single_use ? "Disponibile" : `Generico · usato ${i.uses} volte`;
                    return (
                        <div key={i.code} className="flex items-center justify-between gap-3 py-3">
                            <div className="min-w-0">
                                <div className={`font-mono text-sm ${!i.active || used ? "text-soft line-through" : "text-ink"}`}>{i.code}</div>
                                <div className="text-xs text-muted truncate">{i.note ? `${i.note} · ` : ""}{status}</div>
                            </div>
                            {i.active && !used && (
                                <div className="flex gap-1 shrink-0">
                                    <button onClick={() => copy(i.code)} className="btn-ghost text-xs" title="Copia"><Copy size={14} /></button>
                                    <button onClick={() => disable(i.code)} className="btn-ghost text-xs" title="Disattiva"><Ban size={14} /></button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const Settings = () => {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [prefs, setPrefs] = useState(null);
    const [savingPrefs, setSavingPrefs] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/email-preferences");
                setPrefs(data);
            } catch {}
        })();
    }, []);

    const deleteAccount = async () => {
        setDeleting(true);
        try {
            await api.delete("/account");
            toast.success("Account eliminato.");
            logout();
            nav("/");
        } catch { toast.error("Errore eliminazione"); }
        finally { setDeleting(false); }
    };

    const updatePref = async (key, value) => {
        setPrefs((p) => ({ ...p, [key]: value }));
        setSavingPrefs(true);
        try {
            await api.patch("/email-preferences", { [key]: value });
        } catch {
            toast.error("Errore salvataggio preferenze");
        } finally { setSavingPrefs(false); }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
            <h1 className="font-heading text-4xl text-ink">Impostazioni</h1>

            {user?.is_admin && <InviteCodes />}

            {/* Email notifications */}
            <section className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-6">
                <div className="flex items-center gap-2 mb-1">
                    <Bell size={20} className="text-bordeaux" />
                    <h2 className="font-heading text-2xl text-ink">Notifiche email</h2>
                </div>
                <p className="text-sm text-muted mb-4">Scegli quali eventi vuoi ricevere via email.</p>
                {prefs ? (
                    <div data-testid="email-prefs">
                        <PrefRow
                            label="Nuovi messaggi in chat"
                            hint="Ricevi una email quando qualcuno ti scrive"
                            checked={prefs.messages}
                            onChange={(v) => updatePref("messages", v)}
                            testid="pref-messages"
                        />
                        <PrefRow
                            label="Richieste di collegamento"
                            hint="Nuove richieste e accettazioni"
                            checked={prefs.connections}
                            onChange={(v) => updatePref("connections", v)}
                            testid="pref-connections"
                        />
                        <PrefRow
                            label="Risposte alle tue richieste di aiuto"
                            hint="Quando un professionista risponde nella bacheca"
                            checked={prefs.help_replies}
                            onChange={(v) => updatePref("help_replies", v)}
                            testid="pref-help"
                        />
                        <PrefRow
                            label="Like e commenti (riepilogo)"
                            hint="Ricevi un riepilogo periodico invece di una email per ogni evento"
                            checked={prefs.likes_comments_digest}
                            onChange={(v) => updatePref("likes_comments_digest", v)}
                            testid="pref-digest"
                        />
                        <PrefRow
                            label="Email transazionali"
                            hint="Conferma registrazione — consigliato sempre attivo"
                            checked={prefs.transactional}
                            onChange={(v) => updatePref("transactional", v)}
                            testid="pref-tx"
                        />
                        {savingPrefs && <div className="text-xs text-soft mt-2">Salvataggio…</div>}
                    </div>
                ) : <div className="text-sm text-muted">Caricamento…</div>}
            </section>

            {/* Account */}
            <section className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-6">
                <h2 className="font-heading text-2xl mb-1 text-ink">Account</h2>
                <div className="flex items-center gap-2 mt-3 text-sm text-muted"><Mail size={14} /> {user?.email}</div>
                <div className="flex items-center gap-2 mt-2 text-sm text-muted"><Shield size={14} /> Privacy: profilo visibile agli iscritti</div>
                <button onClick={() => { logout(); nav("/"); }} className="btn-outline text-sm mt-5" data-testid="set-logout">
                    <LogOut size={14} /> Esci dall'account
                </button>
            </section>

            {/* Delete */}
            <section className="bg-paper rounded-2xl border border-red-200 p-6">
                <h2 className="font-heading text-2xl mb-1 text-ink">Diritto all'oblio</h2>
                <p className="text-sm text-muted mb-4">Elimina permanentemente il tuo account e tutti i contenuti collegati. L'operazione è irreversibile.</p>
                {!confirmDelete ? (
                    <button onClick={() => setConfirmDelete(true)} className="btn-danger text-sm" data-testid="set-delete">
                        <Trash2 size={14} /> Elimina il mio account
                    </button>
                ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                        <p className="text-sm text-ink"><strong>Sei sicuro?</strong> Questa azione non può essere annullata.</p>
                        <div className="flex gap-2">
                            <button onClick={deleteAccount} disabled={deleting} className="btn-danger text-sm" data-testid="set-delete-confirm">
                                {deleting ? "Eliminazione…" : "Sì, elimina tutto"}
                            </button>
                            <button onClick={() => setConfirmDelete(false)} className="btn-ghost text-sm">Annulla</button>
                        </div>
                    </div>
                )}
            </section>

            <p className="text-xs text-soft text-center">© 2026 Voci di Imprese · network.vocidiimprese.com</p>
        </div>
    );
};

export default Settings;
